import { EntriesTableSettings } from "../../model/entries-table-settings/EntriesTableSettings";
import { GetUserEntriesTableSettingsDto } from "../../model/entries-table-settings/dto/GetUserEntriesTableSettingsDto";
import { UpdateUserEntriesTableSettingsDto } from "../../model/entries-table-settings/dto/UpdateUserEntriesTableSettingsDto";
import { Entry } from "../../model/entry/Entry";
import { AssignEntryBulkDto } from "../../model/entry/dto/AssignEntryBulkDto";
import { AssignEntryDto } from "../../model/entry/dto/AssignEntryDto";
import { ConvertEntryBulkDto } from "../../model/entry/dto/ConvertEntryBulkDto";
import { CreateEntryDto } from "../../model/entry/dto/CreateNewEntryDto";
import { DeleteEntryBulkDto } from "../../model/entry/dto/DeleteEntryBulkDto";
import { DeleteEntryDto } from "../../model/entry/dto/DeleteEntryDto";
import { ExportEntriesDto } from "../../model/entry/dto/ExportEntriesDto";
import { GetEntriesByFormIdDto } from "../../model/entry/dto/GetEntriesByFormIdDto";
import { GetEntryByIdDto } from "../../model/entry/dto/GetEntryByIdDto";
import { GetEntryLegibleForKanbanCardDto } from "../../model/entry/dto/GetEntryLegibleForKanbanCardDto";
import { GetLatestEntriesDto } from "../../model/entry/dto/GetLatestEntriesDto";
import { ImportEntriesDto } from "../../model/entry/dto/ImportEntriesDto";
import { ImportEntriesDtoResponseDto } from "../../model/entry/dto/ImportEntriesResponseDto";
import { MergeEntriesDto } from "../../model/entry/dto/MergeEntriesDto";
import { RetrieveEntryBulkDto } from "../../model/entry/dto/RetrieveEntryBulkDto";
import { RetrieveEntryDto } from "../../model/entry/dto/RetrieveEntryDto";
import { UpdateEntryDto } from "../../model/entry/dto/UpdateEntryDto";
import { TagEntryBulkDto } from "../../model/entry/dto/TagEntryBulkDto";
import { UpdateEntryTagsDto } from "../../model/entry/dto/UpdateEntryTagsDto";
import { PageResponseDto } from "../../model/meta/PageResponseDto";
import { AtiraApi } from "../AxiosClient";
import { FindDuplicateEntriesDto } from "../../model/entry/dto/FindDuplicateEntriesDto";
import { FindDuplicateEntriesResponseDto } from "../../model/entry/dto/FindDuplicateEntriesResponseDto";
import { FindDuplicateEntriesByIdDto } from "../../model/entry/dto/FindDuplicateEntriesByIdDto";
import { ConvertEntryDto } from "../../model/entry/dto/ConvertEntryDto";

export class EntryAxios {
  static getEntryById(dto: GetEntryByIdDto) {
    return AtiraApi.post<Entry>(`/entry/${dto.entryId}`, dto).then(
      ({ data }) => data,
    );
  }

  static deleteEntry(dto: DeleteEntryDto) {
    return AtiraApi.post<void>(`/entry/${dto.entryId}/delete`, dto).then(
      ({ data }) => data,
    );
  }

  static deleteEntryBulk(dto: DeleteEntryBulkDto) {
    return AtiraApi.post<void>("/entry/delete/bulk", dto).then(
      ({ data }) => data,
    );
  }

  static updateEntryById(dto: UpdateEntryDto) {
    return AtiraApi.patch<void>(`/entry/${dto.entryId}`, dto).then(
      ({ data }) => data,
    );
  }

  static convertEntryById(dto: ConvertEntryDto) {
    return AtiraApi.patch<void>(`/entry/${dto.entryId}/convert`, dto).then(
      ({ data }) => data,
    );
  }

  static getEntriesByFormId(dto: GetEntriesByFormIdDto) {
    return AtiraApi.post<Entry[]>(`/entry/form/${dto.formId}`, dto).then(
      ({ data }) => data,
    );
  }

  static createNewEntry(dto: CreateEntryDto) {
    return AtiraApi.post<void>("/entry/create", dto).then(({ data }) => data);
  }

  static getLatestEntries(dto: GetLatestEntriesDto) {
    return AtiraApi.post<PageResponseDto<Entry[]>>(`/entry/latest`, dto).then(
      ({ data }) => data,
    );
  }

  static convertEntryBulk(dto: ConvertEntryBulkDto) {
    return AtiraApi.patch<void>("/entry/convert/bulk", dto).then(
      ({ data }) => data,
    );
  }

  static getUserTableSettings(dto: GetUserEntriesTableSettingsDto) {
    return AtiraApi.post<EntriesTableSettings>(
      "/entry/table-settings",
      dto,
    ).then(({ data }) => data);
  }

  static updateUserTableSettings(dto: UpdateUserEntriesTableSettingsDto) {
    return AtiraApi.patch<void>(
      `/entry/table-settings/${dto.tableId}`,
      dto,
    ).then(({ data }) => data);
  }

  static getEntriesLegibleForKanbanCard(dto: GetEntryLegibleForKanbanCardDto) {
    return AtiraApi.post<Entry[]>(`/entry/search/kanban-legible`, dto).then(
      ({ data }) => data,
    );
  }

  static exportEntries(dto: ExportEntriesDto) {
    return AtiraApi.post<string>("/entry/data/export", dto).then(
      ({ data }) => data,
    );
  }

  static importEntries(dto: ImportEntriesDto) {
    const formData = new FormData();

    formData.set("userId", dto.userId);
    formData.set("file", dto.file as any);
    formData.set("headersRowIndex", dto.headersRowIndex || "");

    if (dto.headers) {
      formData.set("headers", JSON.stringify(dto.headers));
    }

    return AtiraApi.post<void | ImportEntriesDtoResponseDto>(
      "/entry/data/import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    ).then(({ data }) => data);
  }

  static assignEntry(dto: AssignEntryDto) {
    return AtiraApi.post<void>(
      `/entry/${dto.entryId}/manage-assignees`,
      dto,
    ).then(({ data }) => data);
  }

  static tagEntryBulk(dto: TagEntryBulkDto) {
    return AtiraApi.post<void>("/entry/tag-bulk", dto).then(({ data }) => data);
  }

  static updateEntryTags(dto: UpdateEntryTagsDto) {
    return AtiraApi.patch<void>(`/entry/${dto.entryId}/tags`, dto).then(
      ({ data }) => data,
    );
  }

  static assignEntryBulk(dto: AssignEntryBulkDto) {
    return AtiraApi.post<void>("/entry/assign-bulk", dto).then(
      ({ data }) => data,
    );
  }

  static retrieveEntry(dto: RetrieveEntryDto) {
    return AtiraApi.post(`/entry/${dto.entryId}/delete/undo`, dto).then(
      ({ data }) => data,
    );
  }

  static retrieveEntryBulk(dto: RetrieveEntryBulkDto) {
    return AtiraApi.post("/entry/delete/bulk/undo", dto).then(
      ({ data }) => data,
    );
  }

  static mergeEntries(dto: MergeEntriesDto) {
    return AtiraApi.post<void>("/entry/data/merge", dto).then(
      ({ data }) => data,
    );
  }

  static findPossibleDuplicates(dto: FindDuplicateEntriesDto) {
    return AtiraApi.post<FindDuplicateEntriesResponseDto>(
      `/entry/data/find-duplicates`,
      dto,
    ).then(({ data }) => data);
  }

  static findDuplicatesEntryById(dto: FindDuplicateEntriesByIdDto) {
    return AtiraApi.post<FindDuplicateEntriesResponseDto>(
      `/entry/data/find-duplicates/${dto.entryId}`,
      dto,
    ).then(({ data }) => data);
  }
}
